import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, RotateCcw, ExternalLink } from "lucide-react";
import { Reveal } from "./ui";

const PORTAL = "https://thesashbag-2.myshopify.com/apps/redo/returns-portal/login?referralId=698cb62674c66afd8bff0945";

export const Returns = () => {
  const [open, setOpen] = useState(false);
  return (
    <section id="returns" className="px-6 md:px-12 lg:px-16 pt-8 md:pt-10" data-testid="returns-section">
      <div className="max-w-[960px] mx-auto">
        <Reveal>
          <div className="rounded-3xl border overflow-hidden" style={{ borderColor: "rgba(26,138,126,0.35)", background: "var(--cream)" }}>
            <button
              onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center gap-4 md:gap-6 text-left p-6 md:p-8"
              data-testid="returns-toggle"
              aria-expanded={open}
            >
              <span className="flex-shrink-0 w-12 h-12 rounded-2xl grid place-items-center" style={{ background: "var(--tan)", color: "var(--cream)" }}>
                <RotateCcw size={22} strokeWidth={2} />
              </span>
              <span className="flex-1">
                <span className="eyebrow">Returns · Warranty · Exchanges</span>
                <span className="block mt-1 sash-serif text-[clamp(1.35rem,3vw,2rem)] leading-tight text-[var(--ink)]">
                  Need to return, exchange, or file a warranty claim?
                </span>
              </span>
              <ChevronDown size={26} className="flex-shrink-0 text-[var(--tan-deep)] transition-transform duration-300" style={{ transform: open ? "rotate(180deg)" : "none" }} />
            </button>

            <AnimatePresence initial={false}>
              {open && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
                  style={{ overflow: "hidden" }}
                  data-testid="returns-panel"
                >
                  <div className="px-6 md:px-8 pb-8 md:pb-10 md:pl-[6.5rem]">
                    <p className="text-[1.02rem] leading-relaxed text-[var(--ink-soft)]">
                      We've recently updated our process for returns, warranty issues, and exchanges to make
                      things more streamlined and easier for everyone. To get started, simply submit your request
                      through our returns portal:
                    </p>

                    <a href={PORTAL} target="_blank" rel="noopener noreferrer" className="cta mt-5" data-testid="returns-portal-cta">
                      Open the Returns Portal <ExternalLink size={17} strokeWidth={2.2} />
                    </a>

                    <p className="mt-5 text-[1.02rem] leading-relaxed text-[var(--ink-soft)]">
                      If this is a damaged item that's within our <strong className="text-[var(--ink)]">6-month warranty</strong>, please select <strong className="text-[var(--ink)]">warranty</strong>.
                      Once submitted, you'll receive your return label along with a follow-up email confirmation.
                    </p>

                    <p className="mt-7 font-bold text-[var(--ink)]">A few important things to keep in mind before you ship:</p>
                    <div className="mt-4 space-y-4">
                      <div className="flex gap-3">
                        <span className="feat-check flex-shrink-0">1</span>
                        <p className="text-[1.02rem] leading-relaxed text-[var(--ink-soft)]">
                          <strong className="text-[var(--ink)]">Processing time</strong> — Once we receive your bag and review its condition,
                          returns can take up to <strong className="text-[var(--ink)]">21 days</strong> to process. Please make sure your bag is in new and
                          resellable condition to qualify for a full refund or exchange. Also remember to remove any valuables before
                          shipping it back, as we are not responsible for items left inside.
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <span className="feat-check flex-shrink-0">2</span>
                        <p className="text-[1.02rem] leading-relaxed text-[var(--ink-soft)]">
                          <strong className="text-[var(--ink)]">Refund timeline</strong> — Refunds are processed in order from oldest to newest.
                          Timing can vary and we appreciate your patience as we work through requests as responsibly as possible.
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <span className="feat-check flex-shrink-0">3</span>
                        <p className="text-[1.02rem] leading-relaxed text-[var(--ink-soft)]">
                          <strong className="text-[var(--ink)]">Want a faster option?</strong> — We're happy to offer store credit or an exchange
                          if you'd prefer not to wait on the refund queue.
                        </p>
                      </div>
                    </div>

                    <p className="mt-7 text-[1.02rem] leading-relaxed text-[var(--ink-soft)]">
                      If you have any questions while filling out the form or run into any issues, please don't hesitate to reach out
                      at <a href="mailto:info@thesashbag.com" className="underline font-semibold" style={{ color: "var(--tan-deep)" }}>info@thesashbag.com</a>. We're happy to help!
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
